import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useStore } from "@/lib/store";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getModuleIcon } from "@/utils/module-icons";
import { Loader2, Eye, EyeOff, LayoutGrid } from "lucide-react";
import { standardAllowedModules } from "@shared/module-access";

const MODULE_LABEL_KEYS: Record<string, string> = {
  "offer-builder": "modulePreferences.modules.offer-builder",
  "vsl-creator": "modulePreferences.modules.vsl-creator",
  "ad-creator": "modulePreferences.modules.ad-creator",
  "marketing-analyst": "modulePreferences.modules.marketing-analyst",
  "campaign-creator": "modulePreferences.modules.campaign-creator",
  "reel-creator": "modulePreferences.modules.reel-creator",
  "idea-bank": "modulePreferences.modules.idea-bank",
  "youtube-mode": "modulePreferences.modules.youtube-mode",
};

// Modules that are always hidden from the list (internal/legacy)
const SYSTEM_HIDDEN = new Set(["clone-yourself", "email-creator"]);

interface ModulePreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ModulePreferencesModal({
  open,
  onOpenChange,
}: ModulePreferencesModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { modules } = useStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const allowedModules = standardAllowedModules(user?.allowedModules ?? null);
  const currentHidden: string[] | null = user?.hiddenModules ?? null;

  // Local state for toggles
  const [hiddenSet, setHiddenSet] = useState<Set<string>>(new Set(currentHidden ?? []));

  // Sync local state when modal opens or user data changes
  useEffect(() => {
    if (open) {
      setHiddenSet(new Set(user?.hiddenModules ?? []));
    }
  }, [open, user?.hiddenModules]);

  // Only show modules the user has access to (minus system-hidden ones)
  const accessibleModules = modules.filter((m) => {
    if (SYSTEM_HIDDEN.has(m.id)) return false;
    if (allowedModules === null) return true;
    return allowedModules.includes(m.id);
  });

  const saveMutation = useMutation({
    mutationFn: async (hiddenModules: string[] | null) => {
      return apiRequest("PUT", "/api/user/module-preferences", { hiddenModules });
    },
    onSuccess: () => {
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({
        title: t("modulePreferences.toast.savedTitle"),
        description: t("modulePreferences.toast.savedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("modulePreferences.toast.retryTitle"),
        description: t("modulePreferences.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const toggleModule = (moduleId: string) => {
    setHiddenSet((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        // Don't allow hiding ALL modules
        const wouldBeVisible = accessibleModules.filter(
          (m) => !next.has(m.id) && m.id !== moduleId
        );
        if (wouldBeVisible.length === 0) {
          toast({
            title: t("modulePreferences.toast.cannotHideAllTitle"),
            description: t("modulePreferences.toast.cannotHideAllDesc"),
            variant: "destructive",
          });
          return prev;
        }
        next.add(moduleId);
      }
      return next;
    });
  };

  const handleSave = () => {
    const hidden = Array.from(hiddenSet);
    saveMutation.mutate(hidden.length > 0 ? hidden : null);
  };

  const handleShowAll = () => {
    setHiddenSet(new Set());
  };

  const hasChanges = (() => {
    const currentSet = new Set(currentHidden ?? []);
    if (currentSet.size !== hiddenSet.size) return true;
    for (const id of hiddenSet) {
      if (!currentSet.has(id)) return true;
    }
    return false;
  })();

  const visibleCount = accessibleModules.filter((m) => !hiddenSet.has(m.id)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0c10] border-white/[0.1] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <LayoutGrid className="w-5 h-5 text-red-400" />
            {t("modulePreferences.title")}
          </DialogTitle>
          <p className="text-white/50 text-sm mt-1">{t("modulePreferences.description")}</p>
        </DialogHeader>

        <div className="space-y-1 mt-4 max-h-[400px] overflow-y-auto">
          {accessibleModules.map((module) => {
            const isVisible = !hiddenSet.has(module.id);
            const labelKey = MODULE_LABEL_KEYS[module.id];
            const label = labelKey ? t(labelKey, { defaultValue: module.name }) : module.name;

            return (
              <div
                key={module.id}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                  isVisible ? "bg-white/[0.04]" : "bg-transparent opacity-60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    {getModuleIcon(module.id, "w-5 h-5", "default")}
                  </span>
                  <span className="text-sm font-medium">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {isVisible ? (
                    <Eye className="w-3.5 h-3.5 text-green-400/70" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5 text-white/30" />
                  )}
                  <Switch
                    checked={isVisible}
                    onCheckedChange={() => toggleModule(module.id)}
                    className="data-[state=checked]:bg-red-600"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.08]">
          <div className="flex items-center gap-2">
            <span className="text-white/40 text-xs">
              {t("modulePreferences.visibleCount", {
                visible: visibleCount,
                total: accessibleModules.length,
              })}
            </span>
            {hiddenSet.size > 0 && (
              <button
                onClick={handleShowAll}
                className="text-red-400 hover:text-red-300 text-xs underline transition-colors"
              >
                {t("modulePreferences.showAll")}
              </button>
            )}
          </div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white text-sm px-4"
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {t("modulePreferences.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
